import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Coupon, ProductPrice } from "src/api/types";
import { usePrices } from "src/context/PricesProvider";
import { useProduct } from "src/context/ProductProvider";
import { useIsMobile } from "src/hooks/useIsMobile";
import {
  Box,
  Button,
  Divider,
  lighten,
  Stack,
  Typography,
} from "@mui/material";
import { theme } from "src/assets/theme";
import { handleFocusIntoView } from "src/utils/focusIntoView";
import { Check } from "@mui/icons-material";
import TermsOfConditions from "../TermsOfConditions";
import Header from "../Headers/Header";
import Product from "../Product";
import Subheader from "../Headers/Subheader";
import { useSearchParams } from "react-router-dom";
import PaymentProcessor from "../PaymentProcessor";
import ApplyCoupon from "../ApplyCoupon";
import { useGetPatientCouponsQuery } from "src/api/services/patientCouponsApi";
import CouponView from "../CouponView";
import ProductsDisplay from "src/components/ProductsDisplay/ProductsDisplay";
import { productInformationArray } from "src/constants/producttInformation";
import { useSelectedProduct } from "src/hooks/useSelectedProduct";

const MaleHairLossPaymentInformation = () => {
  const isMobile = useIsMobile();
  const products = usePrices();
  const [searchParams, setSearchParams] = useSearchParams();
  const form = searchParams.get("form")?.toLowerCase();
  const filteredProducts = useMemo(() => {
    if (products == null || products.length === 0) return [];

    return products.filter((p) => p.name.toLowerCase().startsWith(form || ""));
  }, [form, products]);
  const [selectedProduct, setSelectedProduct] = useSelectedProduct();
  const productSlug = useProduct((p) => p.slug);
  const page = "payment-information";
  const { t } = useTranslation();
  const isGlp1 = useProduct((product) => product.is_glp1);
  const billingModel = useProduct((product) => product.billing_model);
  const [coupon, setCoupon] = useState<Coupon | null>(null);
  const { data: coupons = [] } = useGetPatientCouponsQuery({
    product_slug: productSlug,
    redeemed: false,
  });

  const informationArray = productInformationArray["mhl-ot"];

  const handleProductChange = useCallback(
    (product: ProductPrice) => {
      setSelectedProduct(product);
    },
    [setSelectedProduct],
  );

  const defaultProductPrice = useMemo(() => {
    if (selectedProduct == null) {
      return "0";
    }

    const appliedDiscount = coupon?.amount ?? 0;
    const discount =
      coupons.length > 0
        ? coupons.reduce((acc, c) => (acc += c.amount) - appliedDiscount, 0)
        : appliedDiscount;

    const price = Math.max(
      selectedProduct?.amount - discount,
      selectedProduct.amount_limit ?? 0,
    );

    return price.toLocaleString();
  }, [coupon?.amount, coupons, selectedProduct]);

  const saving = useMemo(() => {
    if (selectedProduct == null) return;
    const appliedDiscount = coupon?.amount ?? 0;
    const discount =
      coupons.length > 0
        ? coupons.reduce((acc, c) => (acc += c.amount) - appliedDiscount, 0)
        : appliedDiscount;

    const comparedPrice =
      selectedProduct.number_of_months === 1
        ? 30
        : selectedProduct.monthly_price || 0;

    return selectedProduct.amount - comparedPrice + discount;
  }, [coupon?.amount, coupons, selectedProduct]);

  useEffect(() => {
    if (selectedProduct?.stripe_id != null) {
      searchParams.set("price", selectedProduct.stripe_id);
      setSearchParams(searchParams);
    }
  }, [searchParams, selectedProduct?.stripe_id, setSearchParams]);

  return (
    <Stack gap={3}>
      <Stack gap={2}>
        <Stack gap={3}>
          <Header />
          <Product />
        </Stack>
        <Stack gap={2} onClick={() => handleFocusIntoView("payment-input")}>
          <Typography variant={isMobile ? "body1" : "h6"} fontWeight={600}>
            <Subheader />
          </Typography>
          {informationArray.map((i) => (
            <Box key={i} display="flex" gap={1} alignItems="center">
              <Check
                sx={{ color: theme.palette.primary.main }}
                fontSize="medium"
              />
              <Typography variant={isMobile ? "body2" : "body1"}>
                {t(i)}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Stack>
      <Stack gap={3}>
        <ProductsDisplay
          page={page}
          handleProductChange={handleProductChange}
          products={filteredProducts}
          selectedProduct={selectedProduct}
          form={form}
        />
        <Stack gap={4}>
          <Stack gap={2}>
            <ApplyCoupon setCoupon={setCoupon} coupon={coupon} />
            {coupons.map((c) => (
              <CouponView coupon={c} />
            ))}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography fontSize={"18px"}>
                {t(`${page}.freeShipping`)}
              </Typography>
              <Typography fontSize={"18px"}>{t(`${page}.free`)}</Typography>
            </Stack>
            <Divider />
            <Stack gap={1}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography fontSize={"22px"} fontWeight={600}>
                  {t(`${page}.total`)}
                </Typography>
                <Typography fontSize={"22px"} fontWeight={600}>
                  ${defaultProductPrice}
                </Typography>
              </Stack>
              <Typography
                color={theme.palette.primary.main}
                fontStyle="italic"
                fontWeight={600}
                fontSize={"18px"}
              >
                {t(`${page}.${billingModel}.disclaimer`)}
              </Typography>
            </Stack>
            <Button
              variant="text"
              sx={{
                backgroundColor: lighten(theme.palette.secondary.main, 0.3),
                boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25)",
                color: "#ffffff",
                fontSize: "16px",
                fontWeight: 700,
                borderRadius: "10px",
                textAlign: "center",
              }}
              onClick={() => handleFocusIntoView("payment-input")}
            >
              {t(`${page}.totalSavings`, {
                saving,
              })}
            </Button>
            <Divider sx={{ position: "relative", top: 15 }} />
          </Stack>
          {selectedProduct ? (
            <PaymentProcessor
              product={selectedProduct}
              coupon={coupon}
              dueToday={Number(defaultProductPrice)}
            />
          ) : null}
        </Stack>
      </Stack>
      <TermsOfConditions
        page={page}
        productSlug={productSlug}
        isGlp1={isGlp1}
        numberOfMonths={selectedProduct ? selectedProduct.number_of_months : 1}
      />
    </Stack>
  );
};

export default MaleHairLossPaymentInformation;
